import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import { usePropertyStore } from '@/stores/usePropertyStore'
import SignupView from '@/views/SignupView.vue'
import LoginForm from '@/components/LoginForm.vue'
import PasswordResetDone from '@/components/PasswordResetDone.vue'
import PasswordResetConfirm from '@/components/PasswordResetConfirm.vue'
import PasswordResetForm from '@/components/PasswordResetForm.vue'
import SignupInitial from '@/components/SignupInitial.vue'
import VerifyEmail from '@/components/VerifyEmail.vue'

const dashboardRedirect = async () =>
  (await import('@/router/defaultDashboardRoute')).defaultDashboardRoute()

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/',
      component: { render: () => null },
      beforeEnter: dashboardRedirect,
    },
    {
      path: '/signup/',
      component: SignupView,
      children: [
        {
          path: '',
          component: SignupInitial,
          name: 'signup',
        },
        {
          path: 'verify-email/',
          component: VerifyEmail,
          name: 'verify-email',
        },
        {
          path: 'finish/',
          component: () => import('@/components/FinishSignup.vue'),
          name: 'finish',
        },
      ],
    },
    {
      path: '/login/',
      component: LoginForm,
      name: 'login',
    },
    {
      path: '/password-reset/',
      children: [
        {
          path: '',
          component: PasswordResetForm,
          name: 'password-reset',
        },
        {
          path: 'done/',
          component: PasswordResetDone,
          name: 'password-reset-done',
        },
        {
          path: 'confirm/:uidb64/:token/',
          component: PasswordResetConfirm,
          name: 'password-reset-confirm',
        },
      ],
    },
    {
      path: '/dashboard/',
      component: () => import('@/components/DashboardLayout.vue'),
      children: [
        {
          path: '',
          name: 'dashboard',
          component: { render: () => null },
          meta: {
            title: 'Account Dashboard',
          },
          beforeEnter: async (_to, from, next) => {
            const hash = window.location.hash
            if (!from.name && hash.startsWith('#/')) {
              next('/dashboard' + hash.slice(1))
            } else {
              next(await dashboardRedirect())
            }
          },
        },
        {
          path: 'overview/',
          component: () => import('@/components/AccountOverview.vue'),
          name: 'overview',
          meta: {
            title: 'Account Overview',
          },
        },
        {
          path: 'account/',
          component: () => import('@/components/account/AccountDetail.vue'),
          name: 'account-detail',
          meta: {
            title: 'Manage Account',
          },
        },
        {
          path: 'account/subscription-details/',
          component: () =>
            import('@/components/account/SubscriptionDetails.vue'),
          name: 'subscription-details',
          meta: {
            title: 'Manage Subscription',
          },
        },
        {
          path: 'account/manage/',
          component: () => import('@/components/account/AccountForm.vue'),
          name: 'account-manage',
          meta: {
            title: 'Update Account Details',
          },
        },
        {
          path: 'account/change-email/',
          component: () => import('@/components/account/ChangeEmailForm.vue'),
          name: 'change-email',
          meta: {
            title: 'Change Email',
          },
        },
        {
          path: 'account/delete/',
          component: () => import('@/components/account/DeleteAccountForm.vue'),
          name: 'account-delete',
          meta: {
            title: 'Delete Account',
          },
        },
        {
          path: 'account/subscribe-after-trial/',
          name: 'subscribe-after-trial',
          component: () => import('@/components/account/ConvertFreeTrial.vue'),
          meta: {
            title: 'Continue Building with Stadia Maps',
          },
        },
        {
          path: 'account/add-payment/',
          name: 'account-add-payment',
          component: () => import('@/components/account/AddPayment.vue'),
          meta: {
            title: 'Add Payment Method',
          },
        },
        {
          path: 'account/update-payment/',
          name: 'account-update-payment',
          component: () => import('@/components/account/AddPayment.vue'),
          meta: {
            title: 'Update Payment Method',
          },
        },
        {
          path: 'account/change-password/',
          name: 'password-change',
          component: () => import('@/components/PasswordChangeForm.vue'),
          meta: {
            title: 'Change Password',
          },
        },
        {
          path: 'account/confirm-plan/',
          name: 'confirm-plan',
          component: () => import('@/components/account/ConfirmNewPlan.vue'),
          props: route => ({
            newPlanId: Number(route.query.newPlanId ?? route.params.newPlanId),
            additionalUsage:
              route.query.additionalUsage === 'true' ||
              route.params.additionalUsage === 'true',
            autoConfirm:
              route.query.autoConfirm === 'true' ||
              route.params.autoConfirm === 'true',
          }),
          meta: {
            title: 'Confirm Subscription Changes',
          },
        },
        {
          path: 'account/change-plan/',
          name: 'change-plan',
          component: () => import('@/components/account/SelectPlan.vue'),
          meta: {
            title: 'Change Plan',
          },
          alias: ['plan/'],
        },
        {
          path: 'property/create/',
          name: 'property-create',
          component: () => import('@/components/PropertyCreate.vue'),
          meta: {
            title: 'Create Property',
          },
        },
        {
          path: 'property/:id/',
          component: () => import('@/components/PropertyPanel.vue'),
          beforeEnter: (to, from, next) => {
            const propertyStore = usePropertyStore()
            const id = Number(to.params.id)
            const exists = propertyStore.properties[id] !== undefined
            if (!exists) {
              next({ name: 'overview' })
            } else {
              next()
            }
          },
          children: [
            {
              path: '',
              name: 'property',
              component: () => import('@/components/PropertyView.vue'),
              meta: {
                title: 'Manage Property',
              },
            },
            {
              path: 'edit/',
              name: 'property-edit',
              component: () => import('@/components/PropertyEdit.vue'),
            },
          ],
        },
      ],
    },
  ],
})

router.afterEach((to, from) => {
  const routeTitle = to.meta.title as string | undefined
  if (routeTitle) {
    document.title = `${routeTitle} | Stadia Maps`
  }

  const paq = (window._paq = window._paq || [])
  if (from !== START_LOCATION) {
    paq.push(['setReferrerUrl', from.fullPath])
  }
  paq.push(['setCustomUrl', window.location.href])
  paq.push(['setDocumentTitle', document.title])
  paq.push(['trackPageView'])
  paq.push(['enableLinkTracking'])
})

export default router
